/**
 * Template de prise de besoin : Plan Directeur TI – PME V2
 * Basé sur les 7 piliers BZ + section « Déjà en place / Déjà fait »
 * Les documents générés (Plan directeur, Roadmap, etc.) utilisent 7 dimensions (spider/tableaux), pas 5.
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePdtiPmeV2: IntakeTemplate = {
  id: 'pdti-pme-v2',
  name: 'Prise de besoin Plan Directeur TI – PME V2',
  description:
    'Version basée sur les 7 piliers BZ : Stratégie & Gouvernance TI, Gestion des actifs & parties prenantes, Gestion de projet, Gestion des Services TI, Cybersécurité, Amélioration continue / maturité TI, Innovation & IA. Inclut une section « Déjà en place » pour éviter des recommandations redondantes.',
  documentTypeCode: 'PLAN_DIRECTEUR',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Qualification Client',
      description: "Informations générales sur l'entreprise",
      icon: 'Building2',
      questions: [
        {
          id: 'company_name',
          label: "Nom de l'entreprise",
          type: 'text',
          required: true,
        },
        {
          id: 'company_type',
          label: "Type d'entreprise",
          type: 'select',
          options: ['Startup / Entreprise en démarrage', 'PME établie', 'Entreprise en croissance', 'Organisation'],
          required: true,
        },
        {
          id: 'industry',
          label: "Industrie / Secteur d'activité",
          type: 'select',
          options: [
            "Cabinet d'avocats / Juridique",
            'Cabinet comptable / CPA',
            'Services professionnels (Autre)',
            'Manufacturing / Production',
            'Commerce de détail',
            'Santé',
            'Finance / Assurance',
            'Technologies',
            'Construction',
            'Transport / Logistique',
            'Éducation',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'employees',
          label: "Nombre d'employés",
          type: 'select',
          options: ['Moins de 25 employés', '25-49 employés', '50-100 employés', '100-250 employés', '250 employés et plus'],
          required: true,
        },
        {
          id: 'revenue',
          label: "Chiffre d'affaires annuel (approximatif)",
          type: 'select',
          options: ['Moins de 1M$', '1M$ - 3M$', '3M$ - 10M$', '10M$ - 25M$', '25M$ - 50M$', '50M$ et plus', 'À déterminer'],
          required: false,
        },
        {
          id: 'it_maturity',
          label: 'Maturité TI actuelle',
          type: 'select',
          options: [
            'Débutant - Infrastructure basique, peu de systèmes',
            'Intermédiaire - Systèmes en place, mais peu optimisés',
            'Avancé - Infrastructure solide, processus établis',
            'Expert - TI stratégique, innovation continue',
          ],
          required: true,
        },
        {
          id: 'it_team',
          label: 'Équipe TI actuelle',
          type: 'select',
          options: [
            'Aucune équipe dédiée',
            '1-2 personnes',
            '3-5 personnes',
            '6-10 personnes',
            '10+ personnes',
            'Totalement externalisé',
          ],
          required: true,
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Pilier 1 : Stratégie & Gouvernance TI',
      description: 'Connecter les TI à la stratégie d\'affaires et mesurer la valeur perçue',
      icon: 'Target',
      questions: [
        {
          id: 'main_challenge',
          label: "Quel est votre principal défi TI actuellement ?",
          type: 'textarea',
          placeholder: "Ex: Notre infrastructure est obsolète et cause des interruptions fréquentes...",
          required: true,
        },
        {
          id: 'business_goals',
          label: "Objectifs d'affaires pour les 12 prochains mois",
          type: 'multiselect',
          options: [
            'Croissance des revenus',
            "Amélioration de l'efficacité opérationnelle",
            'Expansion géographique',
            'Nouveaux produits/services',
            'Transformation numérique',
            'Réduction des coûts',
            "Amélioration de l'expérience client",
            'Conformité réglementaire',
          ],
          required: true,
        },
        {
          id: 'it_goals',
          label: 'Objectifs TI prioritaires (alignement stratégie)',
          type: 'multiselect',
          options: [
            "Moderniser l'infrastructure",
            "Améliorer la sécurité",
            'Migration cloud',
            'Automatisation des processus',
            'Business Intelligence / Analytics',
            'Mobilité des employés',
            'Collaboration à distance',
            'Innovation / IA',
            'Réduire les temps d\'arrêt',
            'Optimiser les coûts TI',
          ],
          required: true,
        },
        {
          id: 'timeline',
          label: "Horizon temporel du plan directeur",
          type: 'select',
          options: ['Urgent (0-3 mois)', 'Court terme (3-6 mois)', 'Moyen terme (6-12 mois)', 'Long terme (1-2 ans)', 'Stratégique (2-5 ans)'],
          required: true,
        },
        {
          id: 'success_metrics',
          label: "Comment mesurerez-vous le succès (valeur perçue TI) ?",
          type: 'textarea',
          placeholder: "Ex: Réduction de 50% des temps d'arrêt, augmentation de 30% de la productivité...",
          required: false,
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Pilier 2 : Gestion des actifs & parties prenantes',
      description: "Maîtriser le « qui a quoi » et assurer la conformité sécurité",
      icon: 'Server',
      questions: [
        {
          id: 'current_systems',
          label: 'Systèmes et actifs critiques actuels',
          type: 'multiselect',
          options: [
            'ERP (SAP, Oracle, Dynamics, etc.)',
            'CRM (Salesforce, HubSpot, etc.)',
            'Comptabilité / Finance',
            'Gestion de projet',
            'RH / Paie',
            'E-commerce',
            'Inventory / Supply Chain',
            'Manufacturing Execution System (MES)',
            'Business Intelligence / Analytics',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'infrastructure',
          label: 'Infrastructure actuelle',
          type: 'multiselect',
          options: [
            'Serveurs on-premise',
            'Cloud public (AWS, Azure, GCP)',
            'Cloud privé',
            'Hybride (on-premise + cloud)',
            'Hébergement tiers',
            'SaaS uniquement',
          ],
          required: true,
        },
        {
          id: 'asset_governance',
          label: "Niveau de maîtrise des actifs (inventaire, qui a quoi, conformité)",
          type: 'select',
          options: [
            'Aucun inventaire formalisé',
            'Inventaire partiel ou désuet',
            'Inventaire à jour, contrôles partiels',
            'Inventaire et gouvernance des actifs bien en place',
          ],
          required: true,
        },
        {
          id: 'stakeholders_ti',
          label: 'Parties prenantes clés pour les décisions TI',
          type: 'textarea',
          placeholder: 'Ex: DG, Directeur des opérations, CFO, Responsable RH...',
          required: false,
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Pilier 3 : Gestion de projet',
      description: 'Transformer les idées en valeur mesurable avec gouvernance rigoureuse',
      icon: 'FileText',
      questions: [
        {
          id: 'project_governance',
          label: 'Comment les projets TI sont-ils gérés aujourd\'hui ?',
          type: 'select',
          options: [
            'Ad hoc, peu de suivi formel',
            'Suivi basique (liste, échéances)',
            'Méthodologie légère (priorisation, jalons)',
            'Gouvernance projet structurée (portefeuille, priorités, reporting)',
          ],
          required: true,
        },
        {
          id: 'budget_range',
          label: "Budget estimé pour les investissements TI (plan 3-5 ans)",
          type: 'select',
          options: ['Moins de 25K$', '25K$ - 50K$', '50K$ - 100K$', '100K$ - 250K$', '250K$ - 500K$', '500K$ et plus', 'À déterminer'],
          required: false,
        },
        {
          id: 'budget_3_5y_global',
          label: "Enveloppe budgétaire globale TI 3-5 ans (si connue)",
          type: 'text',
          placeholder: 'Ex: 500K$ sur 5 ans, À déterminer',
          required: false,
        },
        {
          id: 'pain_points',
          label: 'Points de douleur principaux (projets, livraison, valeur)',
          type: 'multiselect',
          options: [
            'Performance lente',
            'Pannes fréquentes',
            'Sécurité insuffisante',
            'Manque d\'intégration entre systèmes',
            'Processus manuels trop nombreux',
            'Difficulté à obtenir des rapports',
            'Support utilisateur insuffisant',
            'Coûts TI trop élevés',
            'Technologie obsolète',
            'Manque de visibilité sur les opérations',
          ],
          required: true,
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Pilier 4 : Gestion des Services TI',
      description: 'Livrer un service de qualité au quotidien selon normes ITIL',
      icon: 'FileText',
      questions: [
        {
          id: 'support_model',
          label: 'Modèle de support actuel / souhaité',
          type: 'select',
          options: [
            'Support réactif - intervention sur demande',
            'Support proactif - surveillance et prévention',
            'Support dédié - ressource à temps plein',
            'Support hybride - mix des approches',
            'Support interne - notre équipe gère',
          ],
          required: true,
        },
        {
          id: 'training_needs',
          label: 'Besoins de formation (utilisateurs, support)',
          type: 'select',
          options: [
            'Formation minimale - utilisateurs autonomes',
            'Formation de base - quelques sessions',
            'Formation complète - programme structuré',
            'Formation continue - support à long terme',
          ],
          required: true,
        },
        {
          id: 'itil_awareness',
          label: 'Niveau de maturité des processus de service (ITIL / bonnes pratiques)',
          type: 'select',
          options: [
            'Aucune pratique formalisée',
            'Quelques processus (incidents, demandes)',
            'Processus en place (incidents, changements, niveaux de service)',
            'Pratiques ITIL / gestion des services bien établies',
          ],
          required: false,
        },
        {
          id: 'additional_info',
          label: 'Informations additionnelles (gouvernance, support)',
          type: 'textarea',
          placeholder: 'Tout ce que nous devrions savoir sur la livraison de services TI...',
          required: false,
        },
      ],
    },
    {
      id: 'phase6',
      title: 'Pilier 5 : Cybersécurité',
      description: "Protéger l'organisation de manière proactive selon NIST 2.0",
      icon: 'Shield',
      questions: [
        {
          id: 'security_concerns',
          label: 'Préoccupations de sécurité',
          type: 'multiselect',
          options: [
            'Protection des données clients',
            'Conformité réglementaire (RGPD, Loi 25, etc.)',
            'Cyberattaques / Ransomware',
            'Accès non autorisés',
            'Perte de données',
            'Backup & Disaster Recovery',
            'Formation des employés à la sécurité',
            'Aucune préoccupation majeure',
          ],
          required: true,
        },
        {
          id: 'priorities',
          label: 'Priorités TI (sélectionnez les 3 principales)',
          type: 'multiselect',
          options: [
            'Sécurité & Conformité',
            'Performance & Disponibilité',
            'Innovation & Compétitivité',
            'Réduction des coûts',
            'Expérience utilisateur',
            'Scalabilité / Croissance',
            'Agilité & Flexibilité',
          ],
          required: true,
          maxSelections: 3,
        },
        {
          id: 'integration_needs',
          label: 'Besoins d\'intégration (sécurité, données)',
          type: 'textarea',
          placeholder: 'Ex: Intégration entre ERP et comptabilité, automatisation des rapports...',
          required: false,
        },
        {
          id: 'users_impacted',
          label: "Nombre d'utilisateurs impactés par les projets TI",
          type: 'select',
          options: ['1-10 utilisateurs', '11-25 utilisateurs', '26-50 utilisateurs', '51-100 utilisateurs', '100+ utilisateurs', 'Toute l\'entreprise'],
          required: true,
        },
      ],
    },
    {
      id: 'phase7',
      title: 'Pilier 6 : Amélioration continue / maturité TI',
      description: "Ne pas subir le changement, le créer activement",
      icon: 'Briefcase',
      questions: [
        {
          id: 'change_readiness',
          label: "L'organisation est-elle prête au changement ?",
          type: 'select',
          options: [
            'Très réticente - beaucoup de résistance attendue',
            'Modérément réticente - un peu de résistance',
            'Neutre - acceptation passive',
            'Ouverte - bonne volonté générale',
            'Très enthousiaste - demande active de changement',
          ],
          required: true,
        },
        {
          id: 'decision_makers',
          label: "Qui sont les décideurs clés pour les projets et l'amélioration TI ?",
          type: 'textarea',
          placeholder: 'Ex: DG, Directeur des opérations, CFO...',
          required: true,
        },
        {
          id: 'stakeholders',
          label: 'Parties prenantes principales (départements concernés)',
          type: 'multiselect',
          options: [
            'Direction générale (C-Suite)',
            'Finances / Comptabilité',
            'Opérations / Production',
            'Ventes / Marketing',
            'RH',
            'Service client',
            'Tous les départements',
          ],
          required: true,
        },
      ],
    },
    {
      id: 'phase8',
      title: 'Pilier 7 : Innovation & IA',
      description: "Adapter l'IA de manière stratégique et éthique",
      icon: 'Target',
      questions: [
        {
          id: 'innovation_ia_goals',
          label: "Objectifs en matière d'innovation et d'IA",
          type: 'multiselect',
          options: [
            'Aucun objectif formel pour l\'instant',
            'Explorer l\'IA (POC, pilotes)',
            'Déployer l\'IA sur des cas d\'usage ciblés',
            'Intégrer l\'IA dans les processus métier',
            'Stratégie IA et gouvernance éthique',
          ],
          required: false,
        },
        {
          id: 'innovation_concerns',
          label: "Préoccupations ou contraintes (éthique, données, compétences)",
          type: 'textarea',
          placeholder: 'Ex: confidentialité des données, compétences internes, coûts...',
          required: false,
        },
      ],
    },
    {
      id: 'phase9',
      title: 'Déjà en place / Déjà fait',
      description: "Indiquez ce qui est déjà en place ou déjà réalisé pour éviter des recommandations redondantes dans le Plan directeur et la Roadmap.",
      icon: 'Shield',
      questions: [
        {
          id: 'deja_en_place_global',
          label: "Éléments déjà en place ou déjà réalisés (par pilier ou en vrac)",
          type: 'textarea',
          placeholder: "Ex: Pilier 1 – Comité TI trimestriel en place. Pilier 5 – Politique de mots de passe, 2FA déployé. Pilier 4 – Catalogue de services documenté. Ou décrire en paragraphe ce qui a déjà été fait pour ne pas le recommander à nouveau.",
          required: false,
          helpText: "Ces informations seront utilisées lors de la génération du Plan directeur et de la Roadmap pour ne pas proposer des recommandations sur des éléments déjà en place.",
        },
        {
          id: 'deja_en_place_strategie',
          label: "Stratégie & Gouvernance TI – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Comité TI, tableau de bord DG, objectifs TI alignés...',
          required: false,
        },
        {
          id: 'deja_en_place_actifs',
          label: "Gestion des actifs & parties prenantes – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Inventaire à jour, CMDB, responsabilités documentées...',
          required: false,
        },
        {
          id: 'deja_en_place_projet',
          label: "Gestion de projet – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Méthodologie projet, portefeuille, priorisation...',
          required: false,
        },
        {
          id: 'deja_en_place_services',
          label: "Gestion des Services TI – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Catalogue de services, SLA, processus incidents/changements...',
          required: false,
        },
        {
          id: 'deja_en_place_cyber',
          label: "Cybersécurité – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Politique sécurité, 2FA, sauvegardes, conformité Loi 25...',
          required: false,
        },
        {
          id: 'deja_en_place_amelioration',
          label: "Amélioration continue / maturité TI – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Revues régulières, indicateurs de maturité, amélioration continue...',
          required: false,
        },
        {
          id: 'deja_en_place_innovation',
          label: "Innovation & IA – Déjà en place",
          type: 'textarea',
          placeholder: 'Ex: Pilotes IA, charte éthique IA, cas d’usage en production...',
          required: false,
        },
      ],
    },
  ],
};
